/**
 * ParentAuthContext.js
 * Contexto de autenticación exclusivo para el portal de padres.
 * Independiente del AuthContext de coaches para evitar conflictos de estado.
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_URL } from '../lib/apiUrl';
import { markReturningUser } from '../lib/pwa';

const STORAGE_KEY = 'mc_parent_token';
const USER_KEY = 'mc_parent_user';

const ParentAuthContext = createContext(null);

export const useParentAuth = () => {
  const ctx = useContext(ParentAuthContext);
  if (!ctx) throw new Error('useParentAuth debe usarse dentro de ParentAuthProvider');
  return ctx;
};

// ─── Helpers de storage ───────────────────────────────────────────────────────
const getStoredToken = () => {
  try { return localStorage.getItem(STORAGE_KEY) || null; } catch { return null; }
};
const getStoredUser = () => {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
};
const persist = (token, user) => {
  try {
    if (token) localStorage.setItem(STORAGE_KEY, token);
    else localStorage.removeItem(STORAGE_KEY);
    if (user) localStorage.setItem(USER_KEY, JSON.stringify(user));
    else localStorage.removeItem(USER_KEY);
  } catch {}
};

// ─── Provider ─────────────────────────────────────────────────────────────────
export const ParentAuthProvider = ({ children }) => {
  const [token, setToken] = useState(() => getStoredToken());
  const [user, setUser] = useState(() => {
    const t = getStoredToken();
    const u = getStoredUser();
    return t && u ? u : null;
  });
  const [loading, setLoading] = useState(false);

  // Sincronizar cabecera Authorization cuando cambia el token
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
    // NO eliminamos la cabecera global para no romper otras llamadas;
    // los endpoints de padres validan el role en el backend.
  }, [token]);

  /**
   * loginParent — guarda token y user del padre tras aceptar invitación.
   * Se llama desde ParentInvitePage tras el POST /parent/accept-invitation.
   */
  const loginParent = useCallback(({ access_token, user: parentUser }) => {
    try { localStorage.removeItem('mc_player_token'); } catch {}
    persist(access_token, parentUser);
    setToken(access_token);
    setUser(parentUser);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    markReturningUser('parent');
  }, []);

  /** loginWithCredentials — login email/contraseña para padres ya registrados. */
  const loginWithCredentials = useCallback(async (email, password) => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/auth/login`, { email, password });
      if (data.user?.role !== 'parent') {
        throw new Error('Esta cuenta no es un acceso de familiar.');
      }
      loginParent({ access_token: data.token, user: data.user });
    } finally {
      setLoading(false);
    }
  }, [loginParent]);

  const logoutParent = useCallback(() => {
    persist(null, null);
    setToken(null);
    setUser(null);
  }, []);

  // Interceptor: si un endpoint del portal de padres devuelve 401/403
  // (token caducado, cuenta eliminada o suspendida), cerramos la sesión del
  // padre y lo enviamos al acceso de familias. Acotado a rutas /parent/ para
  // no interferir con el flujo de coaches.
  useEffect(() => {
    const id = axios.interceptors.response.use(
      (res) => res,
      (error) => {
        const url = error?.config?.url || '';
        const status = error?.response?.status;
        // Excluimos invitación y aceptación: sus 401/403 son errores de acción
        // puntuales (contraseña/email incorrectos), no sesión inválida.
        const isParentEndpoint =
          url.includes('/parent/') &&
          !url.includes('/parent/invitation') &&
          !url.includes('/parent/accept-invitation');
        if (isParentEndpoint && (status === 401 || status === 403) && getStoredToken()) {
          persist(null, null);
          setToken(null);
          setUser(null);
          if (!window.location.pathname.startsWith('/parent/acceso')) {
            window.location.replace('/parent/acceso');
          }
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(id);
  }, []);

  const isAuthenticated = !!token && !!user;

  return (
    <ParentAuthContext.Provider
      value={{ user, token, loading, isAuthenticated, loginParent, loginWithCredentials, logoutParent }}
    >
      {children}
    </ParentAuthContext.Provider>
  );
};

export default ParentAuthContext;
